// web/src/pages/admin/FromEmailAddressesPage.tsx
//
// T21 — Admin CRUD for "from" email addresses (`/api/admin/from-email-addresses`).
// Modeled on PaymentTypesPage.tsx (code/description/checkbox + EntityForm) with
// the delete-confirm flow from SpamAddressesPage.tsx.
//
// NOTE: this manages the `FromEmailAddress` table (used by outbound send-as
// pickers). It is unrelated to `FromEmailTypesPage.tsx`, which targets a
// different legacy lookup table — do not conflate the two.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fromEmailAddressesApi,
  type FromEmailAddress,
} from "@/lib/from-email-addresses";
import { ApiError } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { RowActions } from "@/components/row-actions";
import { Plus } from "lucide-react";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";

function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
  }
  return fallback;
}

const schema = z.object({
  code: z.string().min(1).max(20),
  description: z.string().min(1).max(100),
  emailAddress: z.string().nullable(),
  display: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function FromEmailAddressesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [editing, setEditing] = useState<FromEmailAddress | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["from-email-addresses"],
    queryFn: () => fromEmailAddressesApi.list(),
  });

  const upsert = useMutation({
    mutationFn: async (form: Form) => {
      if (editing) {
        await fromEmailAddressesApi.update(editing.fromEmailAddressId, form);
      } else {
        await fromEmailAddressesApi.create(form);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["from-email-addresses"] });
      toast.success(editing ? "From address updated." : "From address added.");
      setEditing(null);
      setCreating(false);
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Save failed.")),
  });

  const del = useMutation({
    mutationFn: (id: number) => fromEmailAddressesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["from-email-addresses"] });
      toast.success("From address deleted.");
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Delete failed.")),
  });

  async function handleDelete(row: FromEmailAddress) {
    const ok = await confirm({
      title: "Delete from address?",
      body: `${row.code} — ${row.description}`,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) del.mutate(row.fromEmailAddressId);
  }

  const cols: DataGridColumn<FromEmailAddress>[] = [
    { id: "code", header: "Code", accessor: (r) => r.code },
    { id: "description", header: "Description", accessor: (r) => r.description },
    {
      id: "emailAddress",
      header: "Email address",
      accessor: (r) =>
        r.emailAddress && r.emailAddress.trim() !== "" ? (
          r.emailAddress
        ) : (
          <span className="text-muted-foreground">Sender's own address</span>
        ),
    },
    { id: "display", header: "Display", accessor: (r) => (r.display ? "Yes" : "No") },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end pr-2">
          <RowActions
            label={r.code}
            onEdit={() => setEditing(r)}
            onDelete={() => void handleDelete(r)}
            deleteDisabled={del.isPending}
          />
        </div>
      ),
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing
    ? {
        code: editing.code,
        description: editing.description,
        emailAddress: editing.emailAddress,
        display: editing.display,
      }
    : { code: "", description: "", emailAddress: null, display: true };

  return (
    <ListPage backTo="/admin"
      title="From Addresses"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)} aria-label="Add from address">
          <Plus className="mr-2 h-4 w-4" /> Add From Address
        </IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.fromEmailAddressId}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        emptyHeadline="No from addresses found."
        onPageChange={() => undefined}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit From Address" : "Add From Address"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="fea-code">Code</Label>
                <Input id="fea-code" disabled={editing !== null} {...form.register("code")} />
              </div>
              <div>
                <Label htmlFor="fea-desc">Description</Label>
                <Input id="fea-desc" {...form.register("description")} />
              </div>
              <div>
                <Label htmlFor="fea-email">Email address (optional)</Label>
                <Input
                  id="fea-email"
                  placeholder="blank = sender's own address"
                  {...form.register("emailAddress", {
                    setValueAs: (v: string) => (v === "" ? null : v),
                  })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("display")} /> Display
              </label>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
